'use client';

import { useMemo, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import {
  Filter,
  FilteredCounter,
  LifecycleFilter,
  ListPageFilterPanel,
  formatCategoryForDisplay,
} from '@terra/ui-design-system';
import {
  SignalStageSchema,
  getSignalCategoriesForTargetType,
} from '@terra/shared-schemas-orchestrator';
import { useTargetType } from '../../../store/projectStore';
import { CreatedSinceFilterPill } from '../../common/CreatedSinceFilterPill';
import { ListPageDateRangeFilter } from '../../common/ListPageDateRangeFilter';
import { pickFilterOptions } from '../../common/list-page-filter-options';
import type { SignalsListFilters } from '../signals-table/useSignalsListFilters';
import { SignalsAdvancedFilters } from './SignalsAdvancedFilters';
import {
  SIGNAL_PRESENCE_FILTER_VALUES,
  toSignalPresenceFilter,
  toSignalPresenceFilterValue,
} from './signals-filter-options';
import { SignalsStatusFilter } from './SignalsStatusFilter';

type SignalsFilterPanelProps = {
  selection?: ReactNode;
  filters: SignalsListFilters;
  hideLifecycleFilter?: boolean;
  showFilteredCounter: boolean;
  filteredCount: number | undefined;
  totalCount: number | undefined;
};

export function SignalsFilterPanel({
  selection,
  filters,
  hideLifecycleFilter,
  showFilteredCounter,
  filteredCount,
  totalCount,
}: SignalsFilterPanelProps) {
  const t = useTranslations('proxy.signals.filters');
  const tSignals = useTranslations('signals');
  const tStage = useTranslations('designSystem.signals.lifecycle.stage');
  const tFilters = useTranslations('proxy.filters');
  const tEntities = useTranslations('proxy.filteredCounter.entities');
  const targetType = useTargetType();
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const stageItems = useMemo(
    () =>
      SignalStageSchema.options.map((stage) => ({
        value: stage,
        label: tStage(stage),
        tooltip: tSignals(`filters.stageTooltips.${stage}`),
      })),
    [tSignals, tStage],
  );
  const categoryItems = useMemo(
    () =>
      getSignalCategoriesForTargetType(targetType).map((category) => ({
        value: category,
        label: formatCategoryForDisplay(category),
      })),
    [targetType],
  );
  const appWideItems = useMemo(
    () =>
      SIGNAL_PRESENCE_FILTER_VALUES.map((value) => ({
        value,
        label: t(`appWide.${value}`),
      })),
    [t],
  );
  const isAdvancedExpanded = isAdvancedOpen || filters.hasAdvancedFilters;

  return (
    <ListPageFilterPanel
      selection={selection}
      isClearDisabled={!filters.hasActiveFilters}
      onClear={filters.resetFilters}
      addFilter={{
        isActive: isAdvancedExpanded,
        onClick: () => setIsAdvancedOpen((isOpen) => !isOpen),
      }}
      trailing={
        showFilteredCounter ? (
          <FilteredCounter
            filteredCount={filteredCount}
            totalCount={totalCount}
            entityLabel={tEntities('signals')}
          />
        ) : null
      }
      expandedContent={
        isAdvancedExpanded ? <SignalsAdvancedFilters filters={filters} /> : null
      }
    >
      <SignalsStatusFilter
        value={filters.statusFilter}
        onChange={filters.setStatusFilter}
      />
      <Filter
        id="signal-stage"
        density="compact"
        isMultiSelect
        placeholder={t('stage')}
        items={stageItems}
        value={filters.stageFilter}
        onChange={(values) =>
          filters.setStageFilter(
            pickFilterOptions({
              options: stageItems.map((item) => item.value),
              values,
            }),
          )
        }
      />
      <Filter
        id="signal-category"
        density="compact"
        isMultiSelect
        searchable
        placeholder={t('category')}
        items={categoryItems}
        value={filters.categoryFilter}
        onChange={(values) =>
          filters.setCategoryFilter(
            pickFilterOptions({
              options: categoryItems.map((item) => item.value),
              values,
            }),
          )
        }
      />
      {filters.showAppWide ? (
        <Filter
          id="signal-app-wide"
          density="compact"
          placeholder={t('appWide.label')}
          items={appWideItems}
          value={toSignalPresenceFilterValue(filters.appWideFilter)}
          onChange={([value]) =>
            filters.setAppWideFilter(toSignalPresenceFilter(value))
          }
        />
      ) : null}
      <CreatedSinceFilterPill
        createdSince={filters.createdSince}
        onClear={() => filters.setCreatedSince(null)}
      />
      <ListPageDateRangeFilter
        label={tFilters('createdAt')}
        startDate={filters.createdStartDate}
        endDate={filters.createdEndDate}
        onStartDateChange={filters.setCreatedStartDate}
        onEndDateChange={filters.setCreatedEndDate}
      />
      {filters.lifecycle.showLifecycleSelector && !hideLifecycleFilter ? (
        <LifecycleFilter
          value={filters.lifecycleState}
          onValueChange={filters.setLifecycleState}
        />
      ) : null}
    </ListPageFilterPanel>
  );
}
